import {FileUpload} from '@/components/atoms';
import {useCSVFileValidation} from '@/hooks/useCSVFileValidation-hook';
import {useFileUpload} from '@/hooks/useFileUpload';
import {useValidateProductIds} from '@/hooks/useValidateProductIds-hook';
import {useAppDispatch} from '@/store';
import {setStoreListUrl} from '@/store/slices/salesTargetSlice';
import {Constants, NUMBERS} from '@/utils/constants/commonConstants';
import Papa from 'papaparse';
import React, {useState, useEffect, useRef} from 'react';
import './upload-file.scss';
import {ROUTES} from '@/utils/constants/routesConstants';
import getUploadFileHelperText from './getUploadFileHelperText';
import {getSasToken} from '@/services/sasToken';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import UploadErrorMessage from './UploadErrorMessage';
import PropTypes from 'prop-types';
import {logger} from '@/helpers/logger';

export const UploadResult = Object.freeze({
  NONE: 'none',
  SUCCESS: 'success',
  ERROR: 'error'
});

const ERROR_HEADER = Constants.CorruptedFileTitle;
const ERROR_MESSAGE = Constants.CorruptedFileMessage;

/**
 * UploadFile component allows users to upload files with various configurations and validations.
 *
 * @param {Object} props - The properties object.
 * @param {string[]} props.acceptedExtensions - List of accepted file extensions.
 * @param {string} props.description - Description for the file upload.
 * @param {string} props.label - Label for the file upload input.
 * @param {File[]} props.files - Array of files to be uploaded.
 * @param {boolean} [props.multiple=false] - Allow multiple file uploads.
 * @param {string} [props.className=''] - Additional class names for the component.
 * @param {string} [props.containerClassName=''] - Additional class names for the container.
 * @param {boolean} [props.required=false] - Whether the file upload is required.
 * @param {string} props.variant - Variant of the file upload component.
 * @param {string} [props.buttonLabel=Constants.UploadFile] - Label for the upload button.
 * @param {string} props.type - Type of the file upload.
 * @param {string} [props.imageUri=''] - URI of the image to be uploaded.
 * @param {Function} [props.setFile=() => {}] - Function to set the file.
 * @param {Function} [props.uploadFileSuccess=() => {}] - Function to handle successful file upload.
 * @param {Function} props.resetUploadFile - Function to reset the file upload.
 * @param {boolean} [props.isDark=true] - Whether the component is in dark mode.
 * @param {Function} [props.onUpload=() => {}] - Function to handle file upload.
 * @param {string} props.feedbackType - Type of feedback to be displayed.
 * @param {Function} [props.onUploadDelete=() => {}] - Function to handle file upload deletion.
 * @param {string} [props.helperText=''] - Helper text to be displayed.
 * @param {Function} [props.onUploadError=() => {}] - Function to handle file upload errors.
 * @param {boolean} [props.fileSizeCheck=false] - Whether to check the file size.
 * @param {string} [props.dataTestId=''] - Data test ID for the component.
 * @param {boolean} [props.disabled=false] - Whether the file upload is disabled.
 * @param {Function} [props.onSetError=() => {}] - Function to handle file upload errors and to handle upload modal button.
 * @param {Function} [props.setFileErrorMessage=() => {}] - Function to handle file upload errors and to handle upload modal button.
 * @param {Function} [props.setSecondaryBtnText] - Setter for the parent modal secondary button text.
 * @param {boolean} [props.displayOkButton=false] - Whether to show Ok instead of Cancel when some records succeed.
 * @param {boolean} [props.isValidateProductIds=false] - Whether to validate product IDs.
 * @param {Function} [props.validateProductIdsCallback=() => {}] - Function to validate product IDs.
 * @param {Function} [props.customUploadFile = null] - Function to handle the uploadFile with custom code to skip the common file upload flow
 *
 * @returns {JSX.Element} The UploadFile component.
 */

const UploadFile = ({
  acceptedExtensions,
  description,
  label,
  files,
  multiple = false,
  className = '',
  containerClassName = '',
  required = true,
  variant,
  buttonLabel = Constants.UploadFile,
  type,
  imageUri = '',
  setFile = () => {},
  uploadFileSuccess = () => {},
  resetUploadFile,
  isDark = true,
  onUpload = () => {},
  feedbackType,
  onUploadDelete = () => {},
  helperText = '',
  onUploadError = () => {},
  fileSizeCheck = false,
  dataTestId = '',
  disabled = false,
  uploadFor,
  onSetError = () => {},
  getFileUrl,
  isCsvValidationRequired = false,
  setFileErrorMessage = () => {},
  fileType = '',
  customRules = null,
  isValidateProductIds = false,
  validateProductIdsCallback = () => {},
  uploading,
  setUploading,
  customErrorMessages = {},
  customUploadFile = null,
  progressPercentage = null,
  uploadErrorMessage = '',
  setSecondaryBtnText,
  displayOkButton = false
}) => {
  const {uploadFile, progress} = useFileUpload();
  const {onValidateProductIds, validateProductIdsProgress} =
    useValidateProductIds();

  const {validateCSVFile} = useCSVFileValidation();
  const [error, setError] = useState(false);
  const [fileError, setFileError] = useState({});
  const dispatch = useAppDispatch();
  const {uploadFileOnBlob} = useAzureBlob();
  const fallbackProgress = isValidateProductIds
    ? validateProductIdsProgress
    : progress;
  const actualProgressPercentage =
    progressPercentage !== null ? progressPercentage : fallbackProgress;
  const prevUploadErrorMessage = useRef(uploadErrorMessage);
  const onSetErrorRef = useRef(onSetError);

  // Keep ref updated with latest onSetError function
  useEffect(() => {
    onSetErrorRef.current = onSetError;
  }, [onSetError]);

  // Watch for changes in uploadErrorMessage prop
  useEffect(() => {
    // Only run if uploadErrorMessage actually changed
    if (prevUploadErrorMessage.current !== uploadErrorMessage) {
      prevUploadErrorMessage.current = uploadErrorMessage;

      if (uploadErrorMessage) {
        // Handle object error messages
        if (typeof uploadErrorMessage === 'object') {
          setFileError({
            feedbackType: Constants.Error,
            helperText: (
              <UploadErrorMessage uploadErrorMessage={uploadErrorMessage} />
            )
          });
          const processedRecords = uploadErrorMessage?.data?.processedRecords;
          if (
            displayOkButton &&
            typeof processedRecords === 'number' &&
            processedRecords > 0 &&
            typeof setSecondaryBtnText === 'function'
          ) {
            setSecondaryBtnText(Constants.Ok);
          } else if (typeof setSecondaryBtnText === 'function') {
            setSecondaryBtnText(Constants.Cancel);
          }
        } else {
          setFileError({
            feedbackType: Constants.Error,
            helperText: uploadErrorMessage
          });
        }
        onSetErrorRef.current(true);
      } else {
        // Clear error if no message
        setFileError({});
        onSetErrorRef.current(false);
        if (typeof setSecondaryBtnText === 'function') {
          setSecondaryBtnText(Constants.Cancel);
        }
      }
    }
  }, [uploadErrorMessage, setSecondaryBtnText, displayOkButton]);

  const getBase64 = (file, url) => {
    if (!uploadFileSuccess) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      try {
        dispatch(
          uploadFileSuccess({
            title: file.name,
            imageBase64: reader.result,
            imageUri: URL.createObjectURL(file),
            imageUrl: url
          })
        );
      } catch (error) {
        logger.error('File upload error:', error);
        setFileError({
          feedbackType: Constants.Error,
          helperText: 'Failed to process file'
        });
        onSetError(true);
      }
    };
  };

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        resolve(true);
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(true);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(`${ERROR_HEADER.trim()}\n${ERROR_MESSAGE}`));
      };

      img.src = url;
    });
  };

  const handleFileUploadError = (err) => {
    const errorMessage = err?.message || 'File upload failed';
    if (errorMessage.includes('\n')) {
      const [errorHeader, errorDetails] = errorMessage.split('\n');
      setFileError({
        feedbackType: Constants.Error,
        helperText: (
          <div className='flex flex-col space-y-1'>
            <span className='font-semibold'>{errorHeader}</span>
            <span>{errorDetails}</span>
          </div>
        )
      });
    } else {
      setFileError({
        feedbackType: Constants.Error,
        helperText: errorMessage
      });
    }
    onSetError(true);
  };

  const fileUpload = async (file) => {
    try {
      if (file.type.startsWith('image/')) {
        await validateImage(file);
      }

      if (typeof customUploadFile === 'function') {
        onUpload(file);
        setFile([file]);
        await customUploadFile(file, setFileError);

        return;
      }
      let sasResponse = null;
      if (
        uploadFor === Constants.SalesTarget ||
        uploadFor === Constants.BulkRewards ||
        uploadFor === Constants.BulkDigitalCodes ||
        uploadFor === Constants.BulkModifiers
      ) {
        sasResponse = await getSasToken(file.name, uploadFor);
      }
      let response = null;
      if (sasResponse?.blobUrl) {
        response = await uploadFileOnBlob(sasResponse, file);
      } else {
        response = await uploadFile(file, uploadFor, fileType);
      }

      if (!response) throw new Error('No response received from upload');

      const {message, media} = response;

      const handleSuccess = (url, isCsv = false) => {
        if (getFileUrl && isCsv) {
          getFileUrl(url);
        }
        if (isCsv) {
          dispatch(setStoreListUrl(url));
        }
        onUpload(file);
        setFile([file]);
        setUploading(false);
        onSetError(false);
        setFileErrorMessage({feedbackType: '', helperText: ''});
      };

      if (file.type === 'text/csv') {
        const resolvedFileUrl = media?.imageUrl || response?.file?.fileUrl;
        if (message === Constants.Success && resolvedFileUrl) {
          handleSuccess(resolvedFileUrl, true);
          return;
        }
      } else if (message === Constants.Success && media?.imageUrl) {
        getBase64(file, media.imageUrl);
        handleSuccess(media.imageUrl);
        return;
      }

      if (media?.error) throw new Error(media.error);
      throw new Error(Constants.ErrorForFileUpload);
    } catch (err) {
      handleFileUploadError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = () => {
    setError(false);
    setFileError({});
    onSetError(false);
    if (resetUploadFile) {
      dispatch(resetUploadFile());
    }
    setFile([]);
    onUploadDelete();
  };
  const rules = {
    type: 'text/csv',
    size: '25600',
    columns: [
      {
        name: 'Product Id',
        required: true,
        type: 'string',
        minChar: 1,
        maxChar: 255
      }
    ],
    requiredHeaders: ['Product Id']
  };

  const handleFileValidationErrors = (isInvalidFileData, errorsList) => {
    if (isInvalidFileData) {
      setFileError({
        feedbackType: Constants.Error,
        helperText: errorsList.join(', ')
      });
      setUploading(false);
      onSetError(true);
    }
  };

  const handleCsvUpload = (file) => {
    Papa.parse(file, {
      complete: (result) => {
        const rows = result?.data;
        let validationResult;
        if (!customRules) {
          validationResult = validateCSVFile(
            rules,
            {fileType: file?.type, fileSize: file?.size / 1024, csvData: rows},
            'Product Id'
          );
        } else {
          validationResult = validateCSVFile(
            customRules,
            {
              fileType: file?.type,
              fileSize: file?.size / 1024,
              csvData: rows
            },
            'Product ID',
            ROUTES.POINT_MULTIPLIER_PRODUCTS
          );
        }

        const {isInvalidFileData, errorsList} = validationResult;

        if (isInvalidFileData) {
          handleFileValidationErrors(isInvalidFileData, errorsList);
          return;
        }

        if (isValidateProductIds) {
          let key = 'Product Id';
          if (customRules) {
            key = customRules.columns[0]?.name || 'Product Id';
          }
          const productIds = rows.map((row) => row[key]).filter(Boolean);
          if (productIds.length > 0) {
            setUploading(true);

            onValidateProductIds(productIds, (res) => {
              if (Object.keys(res).length !== 0) {
                validateProductIdsCallback({
                  totalProductIds: productIds.length,
                  data: res['data'],
                  fileName: file.name
                });
              } else {
                setFileError({
                  feedbackType: Constants.Error,
                  helperText: 'Product ID validation failed'
                });
                onSetError(true);
              }
              setUploading(false);
            });
          }
        } else {
          setUploading(true);
          fileUpload(file);
        }
      },
      header: true
    });
    setUploading(false);
  };

  const handleUpload = async (file) => {
    setError(false);
    setFileError({feedbackType: Constants.Info, helperText: ''});
    onSetError(false);

    if (file?.type === 'text/csv' && isCsvValidationRequired) {
      handleCsvUpload(file);
      return;
    }

    if (fileSizeCheck) {
      try {
        const fileSize = file.size / NUMBERS.OneThousandTwentyFour;
        if (fileSize > NUMBERS.FiveHundred) {
          setFileError({
            feedbackType: 'error',
            helperText:
              'File cannot be uploaded. File size should be below 500KB'
          });
          onSetError(true);
          return false;
        }
      } catch (err) {
        setFileError({
          feedbackType: 'error',
          helperText: err + '. Supported formats: .jpg,.png'
        });
        onSetError(true);
        return false;
      }
    }

    setUploading(true);
    try {
      if (uploadFor === Constants.SalesTarget) {
        await onUpload(file);
        setUploading(false);
        setFileError({feedbackType: Constants.Info, helperText: ''});
        onSetError(false);
      } else {
        await fileUpload(file);
      }
    } catch (error) {
      setUploading(false);
      setFileError({
        feedbackType: Constants.Error,
        helperText: error?.message || 'File upload failed'
      });
      onSetError(true);
    }
  };

  const handleUploadError = (err) => {
    setError(true);
    setFileError({
      feedbackType: Constants.Error,
      helperText: err
    });
    onUploadError(err);
  };

  const getFeedbackType = () => {
    if (Object.keys(fileError).length > NUMBERS.Zero) {
      return fileError?.feedbackType;
    }
    return feedbackType;
  };

  const isFileError =
    Object.keys(fileError).length > 0 &&
    fileError.feedbackType === Constants.Error;

  return (
    <FileUpload
      data-testid='file-upload-input'
      acceptedExtensions={acceptedExtensions}
      description={description}
      label={label}
      variant={uploading ? Constants.Uploading : variant}
      buttonLabel={buttonLabel}
      onUpload={handleUpload}
      onUploadDelete={handleDelete}
      type={type}
      required={required}
      imageUri={imageUri}
      containerClassName={`${containerClassName} ${error || isFileError ? 'border-2 border-dotted border-red-500' : ''}`}
      className={className}
      multiple={multiple}
      value={isFileError ? '' : files}
      isDark={isDark}
      feedbackType={getFeedbackType()}
      helperText={getUploadFileHelperText({
        variant,
        Constants,
        fileError,
        helperText,
        customErrorMessages
      })}
      onUploadError={handleUploadError}
      dataTestId={dataTestId}
      progressPercentage={actualProgressPercentage} // Use the actual progress here
      disabled={disabled}
    />
  );
};

UploadFile.propTypes = {
  acceptedExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
  description: PropTypes.string,
  label: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  multiple: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  required: PropTypes.bool,
  variant: PropTypes.string,
  buttonLabel: PropTypes.string,
  type: PropTypes.string.isRequired,
  imageUri: PropTypes.string,
  setFile: PropTypes.func,
  uploadFileSuccess: PropTypes.func,
  resetUploadFile: PropTypes.func.isRequired,
  isDark: PropTypes.bool,
  onUpload: PropTypes.func,
  feedbackType: PropTypes.string.isRequired,
  onUploadDelete: PropTypes.func,
  helperText: PropTypes.string,
  onUploadError: PropTypes.func,
  fileSizeCheck: PropTypes.bool,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  uploadFor: PropTypes.string,
  onSetError: PropTypes.func,
  getFileUrl: PropTypes.func,
  isCsvValidationRequired: PropTypes.bool,
  setFileErrorMessage: PropTypes.func,
  fileType: PropTypes.string,
  customRules: PropTypes.object,
  isValidateProductIds: PropTypes.bool,
  validateProductIdsCallback: PropTypes.func,
  uploading: PropTypes.bool,
  setUploading: PropTypes.func,
  customErrorMessages: PropTypes.object,
  customUploadFile: PropTypes.func,
  progressPercentage: PropTypes.number,
  uploadErrorMessage: PropTypes.string,
  setSecondaryBtnText: PropTypes.func,
  displayOkButton: PropTypes.bool
};

export default React.memo(UploadFile);
